import { useState, useEffect, useRef, useCallback } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import {
  Play,
  Pause,
  Square,
  RotateCcw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
  Activity,
  Zap,
  Shield,
  Bot,
  Cpu,
  Route,
  History,
  ChevronDown,
  ChevronUp,
  Trash2,
  Info,
  PowerOff
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import { isAiHealthCheckRunLive } from '@easyflow/shared';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { useChartTokens, type ChartTokens } from '@/hooks/useChartTokens';
import { getErrorMessage } from '../../lib/utils';
import type { LucideIcon } from 'lucide-react';
import type {
  AiHealthCheckProviderKind,
  AiHealthCheckResult,
  AiHealthCheckResultStatus,
  AiHealthCheckRun,
  AiHealthCheckRunWithResults,
  AiHealthCheckStartResponse,
  AiHealthCheckTestType,
  AiHealthCheckVerdict,
} from '@easyflow/shared';

const PROVIDER_TOKEN_KEY: Record<AiHealthCheckProviderKind, keyof ChartTokens> = {
  openai: 'openai',
  gemini: 'google',
  claude: 'claude',
  elevenlabs: 'elevenlabs',
  custom: 'fallback',
};

const PROVIDER_ICONS: Record<AiHealthCheckProviderKind, string> = {
  openai: '🤖',
  gemini: '💎',
  claude: '🧠',
  elevenlabs: '🎙️',
  custom: '🔌',
};

/** Identità visuale di un provider ricavata dai suoi risultati (label e kind viaggiano nei details). */
interface ProviderIdentity {
  ref: string;
  kind: AiHealthCheckProviderKind;
  label: string;
}

function providerIdentity(ref: string, results: AiHealthCheckResult[]): ProviderIdentity {
  const first = results.find((r) => r.provider === ref);
  return {
    ref,
    kind: first?.details.provider_kind ?? 'custom',
    label: first?.details.provider_label ?? ref,
  };
}

type ResultVisualStatus = AiHealthCheckResultStatus | 'pending';

const STATUS_CONFIG: Record<ResultVisualStatus, { color: string; bg: string; border: string; icon: LucideIcon; label: string }> = {
  pass: { color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', icon: CheckCircle, label: 'OK' },
  fail: { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', icon: XCircle, label: 'Errore' },
  warning: { color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: AlertTriangle, label: 'Avviso' },
  skip: { color: 'text-muted-foreground', bg: 'bg-muted/50', border: 'border-border', icon: Info, label: 'Saltato' },
  inactive: { color: 'text-muted-foreground', bg: 'bg-muted/30', border: 'border-border', icon: PowerOff, label: 'Disattivato' },
  pending: { color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: Clock, label: 'In attesa' }
};

const VERDICT_CONFIG: Record<AiHealthCheckVerdict, { gradient: string; icon: LucideIcon; textColor: string }> = {
  excellent: { gradient: 'from-green-500 to-emerald-600', icon: Shield, textColor: 'text-green-600 dark:text-green-400' },
  good: { gradient: 'from-blue-500 to-indigo-600', icon: CheckCircle, textColor: 'text-blue-600 dark:text-blue-400' },
  warning: { gradient: 'from-amber-500 to-orange-600', icon: AlertTriangle, textColor: 'text-amber-600 dark:text-amber-400' },
  critical: { gradient: 'from-red-500 to-rose-600', icon: XCircle, textColor: 'text-red-600 dark:text-red-400' },
  cancelled: { gradient: 'from-gray-400 to-gray-600', icon: Square, textColor: 'text-muted-foreground' }
};

const TEST_TYPE_LABELS: Record<AiHealthCheckTestType, { label: string; icon: LucideIcon }> = {
  connection: { label: 'Connessione API', icon: Zap },
  models: { label: 'Modelli a catalogo', icon: Cpu },
  routing: { label: 'Servizi instradati', icon: Route },
  agents: { label: 'Analisi Agenti', icon: Bot }
};

function getProviderColor(kind: AiHealthCheckProviderKind, tokens: ChartTokens): string {
  return tokens[PROVIDER_TOKEN_KEY[kind]];
}

interface ExtraChartTokens {
  accent: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  success: string;
  warning: string;
  error: string;
  inactive: string;
}

const FALLBACK_EXTRA_TOKENS: ExtraChartTokens = {
  accent: '#3b82f6',
  tooltipBg: 'rgba(255,255,255,0.95)',
  tooltipBorder: 'rgba(0,0,0,0.1)',
  tooltipText: '#1f2937',
  success: '#22c55e',
  warning: '#eab308',
  error: '#dc2626',
  inactive: '#d1d5db',
};

function readExtraChartTokens(): ExtraChartTokens {
  if (typeof document === 'undefined') return FALLBACK_EXTRA_TOKENS;
  const cs = getComputedStyle(document.documentElement);
  const v = (k: string, f: string) => (cs.getPropertyValue(k).trim() || f);
  return {
    accent: v('--chart-accent', FALLBACK_EXTRA_TOKENS.accent),
    tooltipBg: v('--tooltip-bg', FALLBACK_EXTRA_TOKENS.tooltipBg),
    tooltipBorder: v('--tooltip-border', FALLBACK_EXTRA_TOKENS.tooltipBorder),
    tooltipText: v('--tooltip-text', FALLBACK_EXTRA_TOKENS.tooltipText),
    success: v('--status-success', FALLBACK_EXTRA_TOKENS.success),
    warning: v('--status-warning', FALLBACK_EXTRA_TOKENS.warning),
    error: v('--status-error-text', FALLBACK_EXTRA_TOKENS.error),
    inactive: v('--status-inactive', FALLBACK_EXTRA_TOKENS.inactive),
  };
}

function useExtraChartTokens(): ExtraChartTokens {
  const [tokens, setTokens] = useState<ExtraChartTokens>(() => readExtraChartTokens());
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const sync = () => setTokens(readExtraChartTokens());
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(html, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => obs.disconnect();
  }, []);
  return tokens;
}

export default function AIHealthCheck() {
  const { toast } = useToast();
  const baseTokens = useChartTokens();
  const extraTokens = useExtraChartTokens();
  const chartColors = {
    tick: baseTokens.muted,
    grid: baseTokens.grid,
    accent: extraTokens.accent,
    tooltipBg: extraTokens.tooltipBg,
    tooltipBorder: extraTokens.tooltipBorder,
    tooltipText: extraTokens.tooltipText,
    success: extraTokens.success,
    warning: extraTokens.warning,
    error: extraTokens.error,
    fallback: baseTokens.fallback,
    inactive: extraTokens.inactive,
  };
  const [currentRun, setCurrentRun] = useState<AiHealthCheckRun | null>(null);
  const [results, setResults] = useState<AiHealthCheckResult[]>([]);
  const [isStarting, setIsStarting] = useState(false);
  const [history, setHistory] = useState<AiHealthCheckRun[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const pollFnRef = useRef<(runId: string) => Promise<void>>();

  const loadHistory = useCallback(async () => {
    try {
      const res = await apiClient.get<AiHealthCheckRun[]>('/ai-health-check/history');
      if (res.success && res.data) {
        setHistory(res.data);
      }
    } catch {
    }
  }, []);

  const pollStatus = useCallback(async (runId: string) => {
    try {
      const res = await apiClient.get<AiHealthCheckRunWithResults>(`/ai-health-check/status/${runId}`);
      if (res.success && res.data) {
        setCurrentRun(res.data.run);
        setResults(res.data.results);

        if (!isAiHealthCheckRunLive(res.data.run.status)) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          loadHistory();
        }
      }
    } catch {
    }
  }, [loadHistory]);

  useEffect(() => {
    pollFnRef.current = pollStatus;
  }, [pollStatus]);

  const startPolling = useCallback((runId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => pollFnRef.current?.(runId), 2000);
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      const res = await apiClient.get<AiHealthCheckRunWithResults | null>('/ai-health-check/latest');
      if (res.success && res.data) {
        const run = res.data.run;
        setCurrentRun(run);
        setResults(res.data.results);
        if (isAiHealthCheckRunLive(run.status) && !pollRef.current) {
          startPolling(run.id);
        }
      }
    } catch {
    }
  }, [startPolling]);

  useEffect(() => {
    loadLatest();
    loadHistory();
  }, [loadLatest, loadHistory]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startCheck = async () => {
    setIsStarting(true);
    try {
      const res = await apiClient.post<AiHealthCheckStartResponse>('/ai-health-check/start');
      if (res.success && res.data) {
        const data = res.data;
        setResults([]);
        setCurrentRun(data.run);
        setExpandedProviders(new Set());

        startPolling(data.run.id);

        toast({ title: 'Health Check avviato', description: `Test in corso su ${data.providers.length} provider...` });
      } else {
        toast({ title: 'Errore', description: res.error, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setIsStarting(false);
    }
  };

  const pauseCheck = async () => {
    if (!currentRun) return;
    try {
      await apiClient.post(`/ai-health-check/pause/${currentRun.id}`);
      setCurrentRun(prev => prev ? { ...prev, status: 'paused' } : null);
      toast({ title: 'Health Check in pausa' });
    } catch (err) {
      toast({ title: 'Errore pausa', description: getErrorMessage(err) || 'Impossibile mettere in pausa', variant: 'destructive' });
    }
  };

  const resumeCheck = async () => {
    if (!currentRun) return;
    try {
      await apiClient.post(`/ai-health-check/resume/${currentRun.id}`);
      setCurrentRun(prev => prev ? { ...prev, status: 'running' } : null);
      toast({ title: 'Health Check ripreso' });
    } catch (err) {
      toast({ title: 'Errore ripresa', description: getErrorMessage(err) || 'Impossibile riprendere', variant: 'destructive' });
    }
  };

  const stopCheck = async () => {
    if (!currentRun) return;
    try {
      await apiClient.post(`/ai-health-check/stop/${currentRun.id}`);
      setCurrentRun(prev => prev ? { ...prev, status: 'cancelling' } : null);
      toast({ title: 'Health Check in arresto' });
    } catch (err) {
      toast({ title: 'Errore stop', description: getErrorMessage(err) || 'Impossibile fermare', variant: 'destructive' });
    }
  };

  const deleteRun = async (runId: string) => {
    try {
      await apiClient.delete(`/ai-health-check/run/${runId}`);
      if (currentRun?.id === runId) {
        setCurrentRun(null);
        setResults([]);
      }
      loadHistory();
      toast({ title: 'Run eliminata' });
    } catch (err) {
      toast({ title: 'Errore eliminazione', description: getErrorMessage(err) || 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  const loadRunDetails = async (runId: string) => {
    try {
      const res = await apiClient.get<AiHealthCheckRunWithResults>(`/ai-health-check/status/${runId}`);
      if (res.success && res.data) {
        setCurrentRun(res.data.run);
        setResults(res.data.results);
        setShowHistory(false);
      }
    } catch {
    }
  };

  const toggleProvider = (name: string) => {
    setExpandedProviders(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const isRunning = currentRun?.status === 'running' || currentRun?.status === 'cancelling';
  const isPaused = currentRun?.status === 'paused';
  const isStopping = currentRun?.status === 'cancelling';
  const isCompleted = currentRun !== null && !isAiHealthCheckRunLive(currentRun.status);
  const progressPercent = currentRun ? Math.round((currentRun.tested_providers / Math.max(currentRun.total_providers, 1)) * 100) : 0;

  const providerNames = [...new Set(results.map(r => r.provider))];
  const identities = new Map(providerNames.map((ref) => [ref, providerIdentity(ref, results)] as const));
  const identityOf = (ref: string): ProviderIdentity => identities.get(ref) ?? providerIdentity(ref, results);

  const activeProviderNames = providerNames.filter(name => {
    const provResults = results.filter(r => r.provider === name);
    return !provResults.every(r => r.status === 'inactive');
  });

  const inactiveProviderNames = providerNames.filter(name => {
    const provResults = results.filter(r => r.provider === name);
    return provResults.every(r => r.status === 'inactive');
  });

  const radarData = activeProviderNames.map(name => {
    const provResults = results.filter(r => r.provider === name);
    const score = provResults.reduce((acc, r) => {
      if (r.status === 'pass') return acc + 100;
      if (r.status === 'warning') return acc + 50;
      if (r.status === 'skip') return acc + 75;
      return acc;
    }, 0) / Math.max(provResults.length, 1);

    const identity = identityOf(name);
    return {
      provider: `${PROVIDER_ICONS[identity.kind]} ${identity.label}`,
      score: Math.round(score),
      fullMark: 100
    };
  });

  const barData = providerNames.map(name => {
    const provResults = results.filter(r => r.provider === name);
    const isInactive = provResults.every(r => r.status === 'inactive');
    return {
      name: identityOf(name).label,
      pass: provResults.filter(r => r.status === 'pass').length,
      warning: provResults.filter(r => r.status === 'warning').length,
      fail: provResults.filter(r => r.status === 'fail').length,
      skip: provResults.filter(r => r.status === 'skip').length,
      inactive: isInactive ? provResults.length : 0
    };
  });

  const latencyData = results
    .filter(r => r.test_type === 'connection' && r.duration_ms > 0 && r.status !== 'inactive')
    .map(r => ({
      name: identityOf(r.provider).label,
      latency: r.duration_ms,
      color: getProviderColor(identityOf(r.provider).kind, baseTokens)
    }));

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-card border border-border p-6 shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Activity className="h-7 w-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Health Check AI</h2>
              <p className="text-sm text-muted-foreground mt-1">Diagnostica completa di tutti i provider e agenti AI</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isRunning && !isPaused && (
              <Button
                onClick={startCheck}
                disabled={isStarting}
                className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-500/20 border-0"
              >
                {isStarting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                {isStarting ? 'Avvio...' : 'Avvia Health Check'}
              </Button>
            )}
            {isRunning && !isPaused && (
              <>
                <Button
                  onClick={pauseCheck}
                  disabled={isStopping}
                  variant="outline"
                  className="border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 dark:bg-amber-900/20 dark:hover:bg-amber-900/20"
                >
                  <Pause className="h-4 w-4 mr-2" />
                  Pausa
                </Button>
                <Button
                  onClick={stopCheck}
                  disabled={isStopping}
                  variant="outline"
                  className="border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  <Square className="h-4 w-4 mr-2" />
                  {isStopping ? 'Arresto…' : 'Ferma'}
                </Button>
              </>
            )}
            {isPaused && (
              <>
                <Button
                  onClick={resumeCheck}
                  className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground border-0"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Riprendi
                </Button>
                <Button
                  onClick={stopCheck}
                  variant="outline"
                  className="border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Ferma
                </Button>
              </>
            )}
            <Button
              onClick={() => setShowHistory(!showHistory)}
              variant="outline"
              className="border-border text-muted-foreground hover:bg-accent"
            >
              <History className="h-4 w-4 mr-2" />
              Storico
            </Button>
          </div>
        </div>

        {!currentRun && !isRunning && (
          <div className="relative mt-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-foreground">
                  Il diagnostico testerà <strong className="text-foreground">connessione API</strong>, <strong className="text-foreground">modelli disponibili</strong>,
                  <strong className="text-foreground"> servizi instradati</strong> e <strong className="text-foreground">analisi agenti</strong> per ogni provider configurato.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  I test vengono eseguiti in sequenza per provider. Puoi mettere in pausa, riprendere o interrompere in qualsiasi momento.
                  Il tempo stimato dipende dal numero di provider configurati.
                </p>
              </div>
            </div>
          </div>
        )}

        {(isRunning || isPaused) && currentRun && (
          <div className="relative mt-6 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {isPaused ? '⏸️ In pausa' : isStopping ? '⏹️ Arresto in corso...' : '🔄 Test in corso...'} — Provider {currentRun.tested_providers}/{currentRun.total_providers}
              </span>
              <span className="text-muted-foreground">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2 bg-muted" />
            <div className="flex gap-4 text-xs">
              <span className="text-green-600 dark:text-green-400">✓ {currentRun.passed} OK</span>
              <span className="text-amber-600 dark:text-amber-400">⚠ {currentRun.warnings} avvisi</span>
              <span className="text-red-600 dark:text-red-400">✗ {currentRun.failed} errori</span>
            </div>
          </div>
        )}
      </div>

      {showHistory && (
        <div className="rounded-2xl bg-card border border-border p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <History className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            Storico Health Check
          </h3>
          {history.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nessun health check precedente</p>
          ) : (
            <div className="space-y-2">
              {history.map(run => {
                const verdictCfg = VERDICT_CONFIG[run.verdict ?? 'cancelled'];
                return (
                  <div
                    key={run.id}
                    className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border border-border hover:bg-muted transition-colors cursor-pointer group"
                    onClick={() => loadRunDetails(run.id)}
                  >
                    <div className="flex items-center gap-3">
                      <verdictCfg.icon className={`h-5 w-5 ${verdictCfg.textColor}`} />
                      <div>
                        <p className="text-sm text-foreground">
                          {new Date(run.started_at).toLocaleString('it-IT')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {run.total_providers} provider — {run.passed} OK, {run.warnings} avvisi, {run.failed} errori
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {run.verdict && (
                        <Badge variant="outline" className={`${verdictCfg.textColor} border-border`}>
                          {run.verdict}
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        onClick={(e) => { e.stopPropagation(); deleteRun(run.id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isCompleted && currentRun?.verdict && (
        <div className={`rounded-2xl bg-gradient-to-br ${VERDICT_CONFIG[currentRun.verdict].gradient} p-[1px] shadow-2xl`}>
          <div className="rounded-2xl bg-card p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {(() => {
                const VerdictIcon = VERDICT_CONFIG[currentRun.verdict].icon;
                const textColor = VERDICT_CONFIG[currentRun.verdict].textColor;
                return (
                  <>
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${VERDICT_CONFIG[currentRun.verdict].gradient} flex items-center justify-center shadow-lg`}>
                      <VerdictIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className={`text-xl font-bold ${textColor}`}>
                        Verdetto: {currentRun.verdict.charAt(0).toUpperCase() + currentRun.verdict.slice(1)}
                      </h3>
                      <p className="text-muted-foreground mt-1">{currentRun.verdict_message}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Completato: {currentRun.completed_at ? new Date(currentRun.completed_at).toLocaleString('it-IT') : '—'}
                      </p>
                    </div>
                    <div className="flex gap-6 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">{currentRun.passed}</p>
                        <p className="text-xs text-muted-foreground">OK</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{currentRun.warnings}</p>
                        <p className="text-xs text-muted-foreground">Avvisi</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">{currentRun.failed}</p>
                        <p className="text-xs text-muted-foreground">Errori</p>
                      </div>
                      {inactiveProviderNames.length > 0 && (
                        <div>
                          <p className="text-2xl font-bold text-muted-foreground">{inactiveProviderNames.length}</p>
                          <p className="text-xs text-muted-foreground">Disattivati</p>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {radarData.length >= 3 && (
              <div className="rounded-2xl bg-card border border-border p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-foreground mb-4">Punteggio Provider</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={chartColors.grid} />
                    <PolarAngleAxis dataKey="provider" tick={{ fill: chartColors.tick, fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: chartColors.tick, fontSize: 10 }} />
                    <Radar name="Score" dataKey="score" stroke={chartColors.accent} fill={chartColors.accent} fillOpacity={0.2} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="rounded-2xl bg-card border border-border p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-foreground mb-4">Risultati per Provider</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={barData} layout="vertical">
                  <XAxis type="number" tick={{ fill: chartColors.tick, fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: chartColors.tick, fontSize: 12 }} width={80} />
                  <Tooltip
                    contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: '12px', color: chartColors.tooltipText }}
                  />
                  <Bar dataKey="pass" stackId="a" fill={chartColors.success} name="OK" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="warning" stackId="a" fill={chartColors.warning} name="Avvisi" />
                  <Bar dataKey="fail" stackId="a" fill={chartColors.error} name="Errori" />
                  <Bar dataKey="skip" stackId="a" fill={chartColors.fallback} name="Saltati" />
                  <Bar dataKey="inactive" stackId="a" fill={chartColors.inactive} name="Disattivati" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {latencyData.length > 0 && (
            <div className="rounded-2xl bg-card border border-border p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-foreground mb-4">Latenza Connessione (ms)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={latencyData}>
                  <XAxis dataKey="name" tick={{ fill: chartColors.tick, fontSize: 12 }} />
                  <YAxis tick={{ fill: chartColors.tick, fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: '12px', color: chartColors.tooltipText }}
                    formatter={(value) => [`${Number(value ?? 0)}ms`, 'Latenza']}
                  />
                  <Bar dataKey="latency" name="Latenza" radius={[6, 6, 0, 0]}>
                    {latencyData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Dettaglio per Provider
            </h3>

            {providerNames.map(provName => {
              const provResults = results.filter(r => r.provider === provName);
              const isExpanded = expandedProviders.has(provName);
              const isProviderInactive = provResults.every(r => r.status === 'inactive');
              const hasFail = provResults.some(r => r.status === 'fail');
              const hasWarn = provResults.some(r => r.status === 'warning');
              const overallStatus: ResultVisualStatus = isProviderInactive ? 'inactive' : hasFail ? 'fail' : hasWarn ? 'warning' : 'pass';
              const overallCfg = STATUS_CONFIG[overallStatus];
              const OverallIcon = overallCfg.icon;
              const identity = identityOf(provName);
              const providerColor = isProviderInactive ? chartColors.inactive : getProviderColor(identity.kind, baseTokens);

              return (
                <div
                  key={provName}
                  className={`rounded-2xl bg-card border shadow-lg overflow-hidden transition-all duration-300 ${overallCfg.border}`}
                >
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleProvider(provName)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-lg"
                        style={{ backgroundColor: `${providerColor}20`, boxShadow: `0 0 20px ${providerColor}15` }}
                      >
                        {PROVIDER_ICONS[identity.kind]}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className={`font-semibold ${isProviderInactive ? 'text-muted-foreground' : 'text-foreground'}`}>{identity.label}</h4>
                          {identity.kind === 'custom' && (
                            <Badge variant="outline" className="text-xs text-muted-foreground border-border">Custom AI</Badge>
                          )}
                          {isProviderInactive && (
                            <Badge variant="outline" className="text-xs text-muted-foreground border-border">
                              <PowerOff className="h-3 w-3 mr-1" />
                              Disattivato
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{isProviderInactive ? 'Provider non attivo' : `${provResults.length} test eseguiti`}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {provResults.map((r, i) => {
                          const cfg = STATUS_CONFIG[r.status];
                          return (
                            <div
                              key={i}
                              className={`w-3 h-3 rounded-full ${cfg.bg} border ${cfg.border}`}
                              title={`${TEST_TYPE_LABELS[r.test_type].label}: ${cfg.label}`}
                            />
                          );
                        })}
                      </div>
                      <OverallIcon className={`h-5 w-5 ${overallCfg.color}`} />
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-border p-4 space-y-3">
                      {provResults.map((result, i) => {
                        const testCfg = TEST_TYPE_LABELS[result.test_type];
                        const statusCfg = STATUS_CONFIG[result.status];
                        const TestIcon = testCfg.icon;
                        const StatusIcon = statusCfg.icon;

                        return (
                          <div key={i} className={`rounded-xl p-4 ${statusCfg.bg} border ${statusCfg.border}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1">
                                <TestIcon className={`h-5 w-5 mt-0.5 ${statusCfg.color} shrink-0`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-foreground">{testCfg.label}</span>
                                    <Badge variant="outline" className={`text-xs ${statusCfg.color} border-current/20`}>
                                      {statusCfg.label}
                                    </Badge>
                                    {result.duration_ms > 0 && (
                                      <span className="text-xs text-muted-foreground">{result.duration_ms}ms</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-1">{result.message}</p>

                                  {result.details.legs && result.details.legs.length > 0 && (
                                    <div className="mt-3 space-y-1">
                                      <p className="text-xs text-muted-foreground font-medium">Servizi instradati su questo provider:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {result.details.legs.map((leg) => (
                                          <Badge
                                            key={leg.service_key}
                                            variant="outline"
                                            title={`${leg.service_key} — origine: ${leg.source}`}
                                            className={`text-xs ${leg.available ? 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-800' : 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-800'}`}
                                          >
                                            {leg.available ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                                            {leg.label_it}
                                            <span className="ml-1 opacity-60">({leg.model})</span>
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {result.details.agents && result.details.agents.length > 0 && (
                                    <div className="mt-3 space-y-1">
                                      <p className="text-xs text-muted-foreground font-medium">Agenti collegati:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {result.details.agents.map((agent) => (
                                          <Badge
                                            key={agent.id}
                                            variant="outline"
                                            title={agent.kind === 'voice' ? 'Agente vocale' : 'Agente chat'}
                                            className={`text-xs ${agent.is_active ? 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-800' : 'text-muted-foreground border-border'}`}
                                          >
                                            <Bot className="h-3 w-3 mr-1" />
                                            {agent.name}
                                            {agent.model && <span className="ml-1 opacity-60">({agent.model})</span>}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {result.details.found && result.details.found.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-muted-foreground font-medium">Modelli trovati:</p>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {result.details.found.map((m) => (
                                          <Badge key={m} variant="outline" className="text-xs text-green-600 dark:text-green-400 border-green-200 dark:border-green-800">
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                            {m}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {result.details.missing && result.details.missing.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-muted-foreground font-medium">Modelli mancanti:</p>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {result.details.missing.map((m) => (
                                          <Badge key={m} variant="outline" className="text-xs text-red-600 dark:text-red-400 border-red-200 dark:border-red-800">
                                            <XCircle className="h-3 w-3 mr-1" />
                                            {m}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <StatusIcon className={`h-5 w-5 ${statusCfg.color} shrink-0`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
